import { type JSX, type TextareaHTMLAttributes, useId, useState } from 'react';
import {
  getFormControlAriaProps,
  getFormControlErrorId,
  shouldShowError,
} from '@common/components/controls/ariaHelpers/formControlAriaHelpers';
import { type RequiredTestId } from '@common/components/controls/TestId';
import Translated from '@common/components/Translated';
import classnames from '@common/utils/ui/classnames';
import '@common/styles/components/textarea.pcss';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  touched?: boolean;
  error?: string;
  showError?: boolean;
  value?: string;
  tooltip?: string | JSX.Element;
}

function Textarea({
  name,
  value,
  error,
  tooltip,
  touched,
  disabled,
  showError,
  placeholder,
  onFocus,
  onBlur,
  maxLength,
  'data-testid': testId,
  ...rest
}: RequiredTestId<TextareaProps>) {
  const [focused, setFocused] = useState(false);
  const textareaId = useId();
  const showsError = shouldShowError(error, touched, showError);
  const errorId = showsError ? getFormControlErrorId(textareaId) : undefined;
  const tooltipId = tooltip && !showsError ? `${textareaId}-tooltip` : undefined;

  const ariaProps = getFormControlAriaProps({
    invalid: showsError,
    errorId,
    disabled,
    ariaDescribedBy: tooltipId,
  });

  // Match Input component pattern: use ternary for clarity and consistency
  // When error is shown, display error instead of tooltip
  const tooltipContent = showsError && error
    ? <Translated id={error} anchor="validation-error" />
    : tooltip;

  return (
    <label
      className={classnames({
        textarea: true,
        'textarea--state-active': !!value || focused,
        'textarea--state-focused': focused,
        'textarea--state-disabled': disabled,
        'textarea--state-error': touched && !!error,
      })}
    >
      <span
        className={classnames({
          textarea__wrapper: true,
          'textarea__wrapper--labelled': !!placeholder,
        })}
      >
        <span className="textarea__content">
          {placeholder && (
            <span className="textarea__label">
              <Translated id={placeholder} />
            </span>
          )}
          <textarea
            id={textareaId}
            {...ariaProps}
            className="textarea__field"
            name={name}
            value={value}
            onFocus={(e) => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocused(false);
              onBlur?.(e);
            }}
            disabled={disabled}
            maxLength={maxLength}
            data-testid={testId}
            {...rest}
          />
        </span>
      </span>
      {tooltipContent && (
        <span
          className="textarea__tooltip"
          id={showsError ? errorId : tooltipId}
          data-testid="inputTooltip"
        >
          {tooltipContent}
        </span>
      )}
    </label>
  );
}

export default Textarea;
